import type { FollowingFeedItem } from '../api/dataApi';

export interface FeedGroup {
  channel: string;
  channelTitle: string;
  avatarSymbol: string;
  avatarImageKey?: string;
  accentHex: string;
  latestAt: number;
  items: FollowingFeedItem[];
}

// Groups the flat, newest-first feed by channel (groups ordered by their
// newest item) and drops exact repeats (same channel + kind + report number
// / signal type), which can appear when pages overlap.
export function groupFeed(items: FollowingFeedItem[]): FeedGroup[] {
  const seen = new Set<string>();
  const byChannel = new Map<string, FeedGroup>();
  for (const it of items) {
    const key = `${it.channel}|${it.kind}|${it.number ?? it.signalType ?? ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    let g = byChannel.get(it.channel);
    if (!g) {
      g = { channel: it.channel, channelTitle: it.channelTitle, avatarSymbol: it.avatarSymbol, avatarImageKey: it.avatarImageKey, accentHex: it.accentHex, latestAt: it.at, items: [] };
      byChannel.set(it.channel, g);
    }
    g.latestAt = Math.max(g.latestAt, it.at);
    g.items.push(it);
  }
  return [...byChannel.values()].sort((a, b) => b.latestAt - a.latestAt);
}

export function describeItem(it: FollowingFeedItem): string {
  if (it.kind === 'report') return `Report #${it.number}${it.excerpt ? ` — ${it.excerpt}` : ''}`;
  const n = it.count ?? 0;
  const last = it.last !== undefined ? `, latest value ${it.last}` : '';
  return `${n} "${it.signalType}" signal${n === 1 ? '' : 's'} in the last ${it.windowDays ?? 7} days${last}`;
}

export function ago(ms: number, now: number = Date.now()): string {
  const mins = Math.max(0, Math.floor((now - ms) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
